import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Bell, Plus, Pause, Play, RefreshCw, Trash2, Send,
  ChevronDown, ChevronRight, Mail, MessageSquare,
} from 'lucide-react';
import { recurringReminders, admin as adminApi } from '../utils/api';

/**
 * RemindersManager — admin UI for roadmap item #19 Phase 2.
 *
 * Independent of appointment series. Lists all reminders, supports
 * inline create + pause/resume/end/send-now. Body supports the
 * `{{first_name}}`, `{{last_name}}`, `{{full_name}}`, `{{name}}`
 * placeholders that the server substitutes at dispatch.
 *
 * "Run dispatcher" mirrors the appointment-series "Run materializer":
 * useful on hosts without a real cron, or to verify a config.
 */
const RemindersManager = () => {
  const [reminders, setReminders] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [form, setForm] = useState({
    client_id: '',
    title: '',
    body: 'Hi {{first_name}}, just a friendly reminder…',
    channel: 'email',
    frequency: 'monthly',
    interval_n: 1,
    next_send_at: '',
    until_date: '',
    max_sends: '',
  });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        recurringReminders.list(),
        adminApi.getClients(),
      ]);
      setReminders(Array.isArray(r.data) ? r.data : []);
      setClients(Array.isArray(c.data) ? c.data : []);
    } catch (e) {
      toast.error('Could not load reminders');
    } finally { setLoading(false); }
  };

  const refresh = async () => {
    try { const r = await recurringReminders.list(); setReminders(Array.isArray(r.data) ? r.data : []); }
    catch (_) {}
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim() || !form.next_send_at) {
      return toast.error('Title, body, and first-send time are required');
    }
    setCreating(true);
    try {
      const payload = {
        ...form,
        client_id: form.client_id || null,
        interval_n: Number(form.interval_n) || 1,
        max_sends: form.max_sends === '' ? null : Number(form.max_sends),
        until_date: form.until_date || null,
      };
      await recurringReminders.create(payload);
      toast.success('Reminder created');
      setForm({ ...form, title: '', body: 'Hi {{first_name}}, just a friendly reminder…' });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create reminder');
    } finally { setCreating(false); }
  };

  const handlePause   = async (id) => { try { await recurringReminders.pause(id);   toast.success('Paused');   refresh(); } catch (e) { toast.error(e.response?.data?.error || 'Failed'); } };
  const handleResume  = async (id) => { try { await recurringReminders.resume(id);  toast.success('Resumed');  refresh(); } catch (e) { toast.error(e.response?.data?.error || 'Failed'); } };
  const handleEnd     = async (id) => { if (!window.confirm('End this reminder permanently?')) return; try { await recurringReminders.end(id); toast.success('Ended'); refresh(); } catch (e) { toast.error(e.response?.data?.error || 'Failed'); } };
  const handleSendNow = async (id) => {
    try {
      const r = await recurringReminders.sendNow(id);
      toast.success(r.data?.sent ? 'Reminder dispatched' : 'Dispatch failed — see status on the row');
      refresh();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const handleRunAll  = async () => {
    try { const r = await recurringReminders.runAll(); toast.success(`Dispatch: scanned ${r.data?.scanned}, sent ${r.data?.sent}, failed ${r.data?.failed}`); refresh(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const cadenceLabel = (r) => {
    const i = Number(r.interval_n) || 1;
    const map = { weekly: ['week', 'weeks'], monthly: ['month', 'months'], yearly: ['year', 'years'] };
    const [s, p] = map[r.frequency] || map.monthly;
    return i === 1 ? `Every ${s}` : `Every ${i} ${p}`;
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Bell className="text-primary-600" size={22} />
          Recurring Reminders
        </h2>
        <button onClick={handleRunAll} className="btn btn-secondary flex items-center gap-2 text-sm" title="Dispatch all due reminders now">
          <RefreshCw size={14} /> Run dispatcher
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Schedule a templated email/SMS that fires on a regular cadence. Use{' '}
        <code className="bg-gray-100 px-1 rounded">{'{{first_name}}'}</code>,{' '}
        <code className="bg-gray-100 px-1 rounded">{'{{last_name}}'}</code>,{' '}
        <code className="bg-gray-100 px-1 rounded">{'{{full_name}}'}</code> in the body for substitution.
      </p>

      {/* ── Create ───────────────────────────────────────────────── */}
      <form onSubmit={handleCreate} className="border rounded p-4 mb-6 bg-gray-50 space-y-3">
        <div className="font-semibold flex items-center gap-2 text-gray-800">
          <Plus size={16} /> New reminder
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Client (optional)</label>
            <select className="input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
              <option value="">— None (placeholder substitution disabled) —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Title / subject *</label>
            <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Time for your dog's vaccination" />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">Body *</label>
            <textarea className="input" rows="3" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Channel</label>
            <select className="input" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="both">Both</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Frequency</label>
            <select className="input" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Every N {form.frequency === 'yearly' ? 'years' : form.frequency === 'monthly' ? 'months' : 'weeks'}
            </label>
            <input type="number" min="1" className="input" value={form.interval_n} onChange={(e) => setForm({ ...form, interval_n: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">First send at *</label>
            <input type="datetime-local" className="input" value={form.next_send_at} onChange={(e) => setForm({ ...form, next_send_at: e.target.value.replace('T', ' ') })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Stop after (date, optional)</label>
            <input type="date" className="input" value={form.until_date} onChange={(e) => setForm({ ...form, until_date: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Max sends (optional)</label>
            <input type="number" min="1" className="input" value={form.max_sends} onChange={(e) => setForm({ ...form, max_sends: e.target.value })} />
          </div>
        </div>
        <button type="submit" disabled={creating} className="btn btn-primary flex items-center gap-2">
          <Plus size={16} /> {creating ? 'Creating…' : 'Create reminder'}
        </button>
      </form>

      {/* ── List ─────────────────────────────────────────────────── */}
      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : reminders.length === 0 ? (
        <div className="text-sm text-gray-500 italic">No reminders yet.</div>
      ) : (
        <div className="space-y-2">
          {reminders.map((r) => (
            <ReminderRow
              key={r.id}
              reminder={r}
              expanded={expandedId === r.id}
              onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
              cadenceLabel={cadenceLabel(r)}
              onPause={() => handlePause(r.id)}
              onResume={() => handleResume(r.id)}
              onEnd={() => handleEnd(r.id)}
              onSendNow={() => handleSendNow(r.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ReminderRow = ({ reminder: r, expanded, onToggle, cadenceLabel, onPause, onResume, onEnd, onSendNow }) => {
  const statusBadge = {
    active: 'bg-green-100 text-green-800',
    paused: 'bg-yellow-100 text-yellow-800',
    ended:  'bg-gray-200 text-gray-700',
  }[r.status] || 'bg-gray-200 text-gray-700';

  return (
    <div className="border rounded">
      <div className="p-3 flex items-center justify-between gap-3 flex-wrap">
        <button type="button" onClick={onToggle} className="flex items-center gap-2 text-left flex-1 min-w-0">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <div className="min-w-0">
            <div className="font-medium truncate">{r.title}</div>
            <div className="text-xs text-gray-600 truncate">
              {cadenceLabel} · next {r.next_send_at}
              {r.client_first_name ? ` · for ${r.client_first_name} ${r.client_last_name}` : ' · no client'}
              {r.channel === 'email' ? ' · email' : r.channel === 'sms' ? ' · SMS' : ' · email + SMS'}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge}`}>{r.status}</span>
          <span className="text-xs text-gray-500">{r.send_count || 0} sent</span>
          {r.status === 'active' && (
            <button onClick={onPause} className="btn btn-secondary text-xs flex items-center gap-1"><Pause size={12} /> Pause</button>
          )}
          {r.status === 'paused' && (
            <button onClick={onResume} className="btn btn-secondary text-xs flex items-center gap-1"><Play size={12} /> Resume</button>
          )}
          {r.status !== 'ended' && (
            <>
              <button onClick={onSendNow} className="btn btn-secondary text-xs flex items-center gap-1" title="Dispatch this reminder now"><Send size={12} /> Send now</button>
              <button onClick={onEnd} className="btn btn-danger text-xs flex items-center gap-1"><Trash2 size={12} /> End</button>
            </>
          )}
        </div>
      </div>
      {expanded && (
        <div className="border-t bg-gray-50 p-3 text-sm space-y-2">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            {r.channel !== 'sms'   && <span className="flex items-center gap-1"><Mail size={12} /> {r.client_email || '— no email —'}</span>}
            {r.channel !== 'email' && <span className="flex items-center gap-1"><MessageSquare size={12} /> {r.client_phone || '— no phone —'}</span>}
          </div>
          <div>
            <div className="text-xs font-medium text-gray-600 mb-1">Body template</div>
            <pre className="whitespace-pre-wrap bg-white border rounded p-2 text-xs">{r.body}</pre>
          </div>
          {r.last_sent_at && (
            <div className="text-xs text-gray-600">
              <strong>Last sent:</strong> {r.last_sent_at} ({r.last_send_status || '—'})
              {r.last_send_error ? <div className="text-red-700 mt-1">⚠ {r.last_send_error}</div> : null}
            </div>
          )}
          {r.until_date && <div className="text-xs text-gray-600"><strong>Stops after:</strong> {r.until_date}</div>}
          {r.max_sends && <div className="text-xs text-gray-600"><strong>Max sends:</strong> {r.max_sends}</div>}
        </div>
      )}
    </div>
  );
};

export default RemindersManager;
